import React from 'react'
import { createUseStyles } from 'react-jss'
import cn from 'classnames'
import { sliceMarginStyles } from '../../style/span'
import theme from '../../style/theme'
const GlobalVideoSectionSlice = ({ className, slice }) => {
  const classes = useStyles()
  if (!slice) {
    return null
  }
  const { backgroundImage, quote, video } = slice

  // Helper function to get image URL from Sanity image object
  const getImageUrl = (image) => {
    if (!image) return null

    // If it's already a URL string
    if (typeof image === 'string') return image

    // If it's a Sanity image with processed sizes
    if (image.sizes && image.sizes.length > 0) {
      return image.sizes[image.sizes.length - 1].url // Use largest size
    }

    // If it has a direct URL property
    if (image.url) return image.url

    // If it's a Sanity image asset reference, construct URL
    if (image.asset && image.asset._ref) {
      const ref = image.asset._ref
      const projectId = process.env.REACT_APP_SANITY_PROJECT_ID || 'bhil6ydp'
      const dataset = process.env.REACT_APP_SANITY_DATASET || 'staging'
      // Parse the reference to get the image details
      const parts = ref.replace('image-', '').split('-')
      if (parts.length >= 3) {
        const hash = parts[0]
        const dimensions = parts[1]
        const format = parts[2]
        return `https://cdn.sanity.io/images/${projectId}/${dataset}/${hash}-${dimensions}.${format}`
      }
    }

    return null
  }

  // Helper function to extract YouTube video ID
  const extractYouTubeId = (url) => {
    if (!url) return null
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/
    const match = url.match(regExp)
    return (match && match[2].length === 11) ? match[2] : null
  }

  // Helper function to extract Instagram post ID
  const extractInstagramId = (url) => {
    if (!url) return null
    const regExp = /instagram\.com\/p\/([A-Za-z0-9_-]+)\/?/
    const match = url.match(regExp)
    return match ? match[1] : null
  }

  const backgroundImageUrl = getImageUrl(backgroundImage)

  return (
    <section className={cn(classes.videoSection, className)}>
      {backgroundImageUrl && (
        <div
          className={classes.videoBackground}
          style={{
            backgroundImage: `url(${backgroundImageUrl})`
          }}
        />
      )}

      <div className={classes.videoContent}>
        {quote?.text && (
          <div className={classes.textContent}>
            {quote.text}
          </div>
        )}

        {video && (
          <div className={classes.videoContainer}>
            {video.youtubeUrl ? (
              <div className={classes.youtubeEmbed}>
                <iframe
                  src={`https://www.youtube.com/embed/${extractYouTubeId(video.youtubeUrl)}`}
                  title={video.title || 'Video'}
                  frameBorder='0'
                  allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                  allowFullScreen
                />
              </div>
            ) : video.instagramUrl ? (
              <div className={classes.instagramEmbed}>
                <iframe
                  src={`https://www.instagram.com/p/${extractInstagramId(video.instagramUrl)}/embed/`}
                  title={video.title || 'Instagram Video'}
                  frameBorder='0'
                  scrolling='no'
                  allowTransparency='true'
                  allowFullScreen
                />
              </div>
            ) : video.videoFile ? (
              <video
                className={classes.videoPlayer}
                controls
                poster={video.poster ? getImageUrl(video.poster) : undefined}
              >
                <source src={video.videoFile.url} type='video/mp4' />
                Your browser does not support the video tag.
              </video>
            ) : null}
          </div>
        )}
      </div>
    </section>
  )
}

const useStyles = createUseStyles({
  videoSection: {
    extend: [sliceMarginStyles],
    position: 'relative',
    padding: '2.5rem 4rem 15.6rem 4rem',
    backgroundColor: theme.colors.backgroundColors?.default?.background || '#f8f9fa',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      padding: '26px 22px',
      minHeight: '70vh'
    }
  },
  videoBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    opacity: 1,
    zIndex: 1
  },
  videoContent: {
    position: 'relative',
    zIndex: 2,
    margin: '0 auto',
    textAlign: 'center'
  },
  sectionTitle: {
    fontSize: '2.5rem',
    color: '#D9E1CC',
    marginBottom: '2rem',
    fontFamily: theme.fonts.headings,
    [theme.breakpoints.down('md')]: {
      fontSize: '1.8rem'
    }
  },
  textContent: {
    fontSize: '36px',
    color: '#D9E1CC',
    padding: '0',
    lineHeight: 1.4,
    fontFamily: theme.fonts.headings,
    position: 'relative',
    zIndex: 2,
    margin: '0 0 7.5rem 0',
    textAlign: 'left',
    [theme.breakpoints.down('md')]: {
      fontSize: '20px',
      margin: '0 0 3rem 0'
    }
  },
  videoContainer: {
    marginTop: '2rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: '800px'
  },
  youtubeEmbed: {
    position: 'relative',
    paddingBottom: '56.25%',
    height: 0,
    overflow: 'hidden',
    borderRadius: '8px',
    '& iframe': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none'
    }
  },
  instagramEmbed: {
    position: 'relative',
    width: '100%',
    maxWidth: '540px',
    margin: '0 auto',
    backgroundColor: '#fff',
    borderRadius: '8px',
    overflow: 'hidden',
    '& iframe': {
      width: '100%',
      height: '100%',
      minHeight: '500px',
      border: 'none',
      [theme.breakpoints.down('md')]: {
        minHeight: '400px'
      },
      [theme.breakpoints.down('sm')]: {
        minHeight: '390px'
      }
    }
  },
  videoPlayer: {
    width: '100%',
    maxWidth: '800px',
    height: 'auto',
    borderRadius: '8px'
  }
}, { name: 'GlobalVideoSectionSlice' })

export default GlobalVideoSectionSlice
